/**
 * پنل مدیریت — نبض بازار
 * ========================
 * دو کار: سلامتِ منابع داده (مدیر باید ببیند داده مال چه زمانی است)، و
 * مدیریت رویدادهای تقویم اقتصادی — که تا وقتی کلید Trading Economics وارد
 * نشده، دستی است.
 *
 * ورودی زمان، **به وقت تهران** گرفته و هنگام ذخیره به UTC تبدیل می‌شود؛
 * مدیر ایرانی ساعت نیویورک را حساب نمی‌کند، ولی دیتابیس همیشه UTC می‌ماند
 * تا رویداد دستی و رویدادِ همگام‌شده یک معنی داشته باشند.
 */
import { useEffect, useState } from 'react'
import { Activity, CalendarDays, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { PageHead } from '../AdminLayout'
import { Panel, DataTable, RefreshButton, ConfirmButton, Drawer, Tag } from '../ui'
import { get, post, patch, del } from '../../lib/api'
import { toast } from '../../store'
import { clsx, fa, num, timeAgo } from '../../lib/format'
import { COUNTRIES, IMPORTANCE, usd } from '../../lib/pulse'

const INDICATORS: [string, string][] = [
  ['CPI', 'تورم (CPI)'], ['PPI', 'تورم تولید (PPI)'], ['RATE', 'نرخ بهره'],
  ['NFP', 'اشتغال (NFP)'], ['UNEMP', 'نرخ بیکاری'], ['GDP', 'GDP'],
  ['RETAIL', 'خرده‌فروشی'], ['PMI', 'PMI'], ['FOMC', 'نشست FOMC'],
  ['SPEECH', 'سخنرانی'], ['OTHER', 'سایر'],
]

/** تهران → UTC و برعکس؛ +۳:۳۰ ثابت (تغییر فصلی از ۱۴۰۱ حذف شده) */
const TEHRAN_MIN = 3.5 * 60
const toUtcIso = (local: string) => {
  const d = new Date(local.replace(' ', 'T') + 'Z')          // به‌عنوان UTC بخوان
  if (isNaN(+d)) return ''
  return new Date(+d - TEHRAN_MIN * 60_000).toISOString().slice(0, 16)
}
/** نمایش زمان به وقت تهران — مستقل از منطقه زمانی مرورگرِ مدیر */
const tehranStamp = (iso: string): string => {
  const d = new Date(iso)
  if (isNaN(+d)) return '—'
  return new Intl.DateTimeFormat('fa-IR', {
    timeZone: 'Asia/Tehran', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d)
}

const toTehranInput = (iso: string) => {
  const d = new Date(iso)
  if (isNaN(+d)) return ''
  return new Date(+d + TEHRAN_MIN * 60_000).toISOString().slice(0, 16)
}

export default function PulseAdmin() {
  const [status, setStatus] = useState<any>(null)
  const [rows, setRows] = useState<any[] | null>(null)
  const [edit, setEdit] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)

  const load = () => {
    setLoading(true)
    Promise.all([
      get('/admin/system/pulse/status').then(setStatus).catch(() => setStatus(null)),
      get('/admin/system/pulse/events').then(setRows).catch(() => setRows([])),
    ]).finally(() => setLoading(false))
  }
  useEffect(load, [])

  const save = async () => {
    const at = toUtcIso(edit.atLocal)
    if (!at) return toast.err('زمان رویداد را وارد کنید')
    const body = {
      event_at: at, country: edit.country, title: edit.title,
      indicator: edit.indicator, importance: Number(edit.importance) || 2,
      currency: edit.currency || 'USD', previous: edit.previous || '',
      forecast: edit.forecast || '', actual: edit.actual || '',
      assets: edit.assets || 'BTC,ETH,USD', note: edit.note || '',
    }
    try {
      if (edit.id) await patch(`/admin/system/pulse/events/${edit.id}`, body)
      else await post('/admin/system/pulse/events', body)
      toast.ok('ذخیره شد'); setEdit(null); load()
    } catch (e: any) { toast.err(e.message) }
  }

  const sync = async () => {
    setSyncing(true)
    try {
      const r: any = await post('/admin/system/pulse/sync')
      toast.ok(`${fa(r.count)} رویداد همگام شد`); load()
    } catch (e: any) { toast.err(e.message) } finally { setSyncing(false) }
  }

  const st = status?.state || {}

  return (
    <>
      <PageHead title="نبض بازار" subtitle="سلامت منابع داده و مدیریت تقویم اقتصادی"
        action={
          <div className="flex gap-2">
            <RefreshButton onClick={load} loading={loading} />
            <button onClick={sync} disabled={syncing} className="btn-ghost !py-2 text-xs">
              <RefreshCw className={clsx('h-3.5 w-3.5', syncing && 'animate-spin')} /> همگام‌سازی تقویم
            </button>
            <button className="btn-primary !py-2 text-xs"
              onClick={() => setEdit({
                atLocal: '', country: 'US', title: '', indicator: 'CPI', importance: 3,
                currency: 'USD', previous: '', forecast: '', actual: '',
                assets: 'BTC,ETH,XAU,USD', note: '',
              })}>
              <Plus className="h-3.5 w-3.5" /> رویداد جدید
            </button>
          </div>
        } />

      {/* ── سلامت منابع */}
      <Panel title="وضعیت داده‌ها" dense>
        <div className="grid gap-3 p-3 sm:grid-cols-3 lg:grid-cols-6">
          {[
            ['ارزش کل بازار', usd(st.total_mcap)],
            ['دامیننس بیت‌کوین', st.btc_d >= 0 ? `${num(st.btc_d, 1)}٪` : '—'],
            ['دامیننس تتر', st.usdt_d >= 0 ? `${num(st.usdt_d, 2)}٪` : '—'],
            ['ترس و طمع', st.fng >= 0 ? fa(st.fng) : '—'],
            ['فصل آلت‌کوین', st.altseason >= 0 ? fa(Math.round(st.altseason)) : '—'],
            ['رژیم بازار', st.regime === 'risk_on' ? 'ریسک‌پذیری'
              : st.regime === 'risk_off' ? 'ریسک‌گریزی' : st.regime === 'neutral' ? 'خنثی' : '—'],
          ].map(([l, v]) => (
            <div key={l as string} className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
              <div className="text-[10px] text-slate-500">{l}</div>
              <div className="num mt-1 text-sm font-bold text-white" dir="ltr">{v}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3 border-t border-white/[.06] px-3 py-2 text-[11px] text-slate-500">
          {Object.entries(status?.feeds || {}).map(([k, ts]: [string, any]) => (
            <span key={k} className="flex items-center gap-1">
              <Activity className="h-3 w-3 text-up" />
              {{ global: 'شاخص‌های کلان', markets: 'بازار ارزها', fng: 'ترس و طمع', calendar: 'تقویم' }[k] || k}
              : {timeAgo(new Date(ts * 1000).toISOString())}
            </span>
          ))}
          {status?.state?.sources && Object.keys(status.state.sources).length > 0 && (
            <span className="text-slate-600">
              منبع فعال: {Object.entries(status.state.sources).map(([k, v]) =>
                `${{ global: 'شاخص‌ها', markets: 'بازار' }[k] || k}=${
                  { coingecko: 'CoinGecko', coinpaprika: 'CoinPaprika', internal: 'داده خود ارزینو' }[v as string] || v}`).join('، ')}
            </span>
          )}
          {status && !Object.keys(status.feeds || {}).length && (
            <span className="text-gold">هنوز داده‌ای از منابع بیرونی نرسیده است</span>
          )}
          {status?.lastError && <span className="text-down" dir="ltr">{status.lastError}</span>}
          {!status?.teKeySet && (
            <span className="ms-auto">
              تقویم هر ۶ ساعت خودکار همگام می‌شود (فید رایگان). «مقدار واقعی» را پس از
              انتشار دستی وارد کنید، یا با کلید Trading Economics آن هم خودکار می‌شود.
            </span>
          )}
        </div>
      </Panel>

      {/* ── رویدادها */}
      <div className="mt-4">
        <Panel title="رویدادهای تقویم اقتصادی (۲ روز گذشته تا ۲ هفته آینده)" dense>
          <DataTable rows={rows} loading={loading} minWidth={860} empty="رویدادی ثبت نشده است"
            onRowClick={(r: any) => setEdit({ ...r, atLocal: toTehranInput(r.at) })}
            columns={[
              {
                key: 'at', header: 'زمان (تهران)', cell: (r: any) => (
                  // faDate/faTime به وقتِ مرورگر است؛ مدیری که از خارج وارد
                  // شده، زیر عنوان «تهران» ساعتِ خودش را می‌دید و با فرمِ
                  // ویرایش (که تهرانی است) دو زمانِ متفاوت می‌گرفت.
                  <span className="num text-slate-300">{tehranStamp(r.at)}</span>
                ),
              },
              {
                key: 'c', header: 'کشور', cell: (r: any) => (
                  <span>{COUNTRIES[r.country]?.flag || '🌐'} {COUNTRIES[r.country]?.fa || r.country}</span>
                ),
              },
              { key: 't', header: 'رویداد', cell: (r: any) => <span className="text-slate-100">{r.title || r.indicator}</span> },
              {
                key: 'i', header: 'اهمیت', cell: (r: any) => (
                  <span className="text-[11px]" style={{ color: IMPORTANCE[r.importance]?.color }}>
                    ● {IMPORTANCE[r.importance]?.fa}
                  </span>
                ),
              },
              {
                key: 'v', header: 'قبلی / پیش‌بینی / واقعی', cell: (r: any) => (
                  <span className="num text-[11px] text-slate-400" dir="ltr">
                    {r.previous || '—'} / {r.forecast || '—'} / {r.actual || '—'}
                  </span>
                ),
              },
              {
                key: 's', header: 'منبع', cell: (r: any) => (
                  <Tag kind={r.source === 'te' ? 'info' : 'muted'}>{r.source === 'te' ? 'خودکار' : 'دستی'}</Tag>
                ),
              },
              {
                key: 'x', header: '', cell: (r: any) => (
                  <div onClick={(e) => e.stopPropagation()}>
                    <ConfirmButton variant="ghost" label="حذف" question="حذف؟"
                      icon={<Trash2 className="h-3.5 w-3.5" />}
                      onConfirm={async () => {
                        try { await del(`/admin/system/pulse/events/${r.id}`); toast.ok('حذف شد'); load() }
                        catch (e: any) { toast.err(e.message) }
                      }} />
                  </div>
                ),
              },
            ]} />
        </Panel>
      </div>

      {/* ── فرم رویداد */}
      <Drawer open={!!edit} onClose={() => setEdit(null)}
        title={edit?.id ? 'ویرایش رویداد' : 'رویداد جدید'}>
        {edit && (
          <>
            <label className="label">زمان رویداد — به وقت تهران</label>
            <input type="datetime-local" className="input num mb-3" dir="ltr" value={edit.atLocal}
              onChange={(e) => setEdit({ ...edit, atLocal: e.target.value })} />

            <label className="label">شاخص</label>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {INDICATORS.map(([v, l]) => (
                <button key={v} onClick={() => setEdit({ ...edit, indicator: v })}
                  className={clsx('chip', edit.indicator === v && 'border-brand-500/50 bg-brand-500/15 text-brand-200')}>
                  {l}
                </button>
              ))}
            </div>

            <label className="label">عنوان نمایشی (فارسی)</label>
            <input className="input mb-3" placeholder="مثلاً: انتشار CPI آمریکا" value={edit.title}
              onChange={(e) => setEdit({ ...edit, title: e.target.value })} />

            <div className="mb-3 grid grid-cols-2 gap-2">
              <div>
                <label className="label">کشور</label>
                <select className="input" value={edit.country}
                  onChange={(e) => setEdit({ ...edit, country: e.target.value })}>
                  {Object.entries(COUNTRIES).map(([code, c]) => (
                    <option key={code} value={code} className="bg-ink-900">{c.flag} {c.fa}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">اهمیت</label>
                <select className="input" value={edit.importance}
                  onChange={(e) => setEdit({ ...edit, importance: Number(e.target.value) })}>
                  <option value={1} className="bg-ink-900">کم</option>
                  <option value={2} className="bg-ink-900">متوسط</option>
                  <option value={3} className="bg-ink-900">زیاد</option>
                </select>
              </div>
            </div>

            <div className="mb-3 grid grid-cols-3 gap-2">
              {[['previous', 'قبلی'], ['forecast', 'پیش‌بینی'], ['actual', 'واقعی']].map(([k, l]) => (
                <div key={k}>
                  <label className="label !text-[10px]">{l}</label>
                  <input className="input num !py-2 text-xs" dir="ltr" placeholder="2.6%"
                    value={edit[k] || ''} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} />
                </div>
              ))}
            </div>
            <p className="mb-3 text-[10px] leading-5 text-slate-600">
              «واقعی» را پس از انتشار وارد کنید — اختلاف با پیش‌بینی و جهتِ اثر، خودکار
              محاسبه و نمایش داده می‌شود. واحدها: ٪، K، M، B.
            </p>

            <label className="label">دارایی‌های حساس (با کاما)</label>
            <input className="input num mb-3" dir="ltr" value={edit.assets}
              onChange={(e) => setEdit({ ...edit, assets: e.target.value })} />

            <label className="label">تحلیل دستی (اختیاری — جای متن خودکار می‌نشیند)</label>
            <textarea className="input mb-4 min-h-[70px]" value={edit.note}
              onChange={(e) => setEdit({ ...edit, note: e.target.value })} />

            <button onClick={save} disabled={!edit.atLocal} className="btn-primary w-full !py-3">
              <CalendarDays className="h-4 w-4" /> ذخیره رویداد
            </button>
          </>
        )}
      </Drawer>
    </>
  )
}
